import { useState, useRef, useEffect } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { Mail, Phone, MapPin, Calendar, Lock, Camera, Save, X, Edit2, Check, Shield, LogOut, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useOrders } from '../../hooks/useOrders';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/auth.service';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import { SuccessModal } from '../shared/SuccessModal';
import { DEFAULT_AVATAR_URL } from '../../constants/avatar';

interface ProfilePageProps {
  onNavigate?: (page: string) => void;
}

export function ProfilePage({ onNavigate }: ProfilePageProps) {
  const { user, updateUser } = useAuth();
  const { orders: ordersData } = useOrders({ page: 1, limit: 50 });
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modal states
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState({ title: '', message: '' });
  const [isLoadingPasswordChange, setIsLoadingPasswordChange] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const buildProfileData = (currentUser: typeof user) => ({
    name: currentUser?.name || 'Guest User',
    email: currentUser?.email || 'guest@restaurant.com',
    phone: currentUser?.phone || '',
    role: currentUser?.role || 'viewer',
    bio: currentUser?.bio || '',
    location: currentUser?.location || '',
    joinDate: currentUser?.createdAt || new Date().toISOString().split('T')[0],
    profileImage: currentUser?.avatar || DEFAULT_AVATAR_URL,
  });

  // Profile data state - initialize from logged-in user or default
  const [profileData, setProfileData] = useState(buildProfileData(user));

  // Separate state for form editing
  const [formData, setFormData] = useState(profileData);

  useEffect(() => {
    if (!user) return;

    const nextProfileData = buildProfileData(user);
    setProfileData(nextProfileData);

    // Only overwrite form state when not actively editing.
    if (!isEditing) {
      setFormData(nextProfileData);
    }
  }, [user, isEditing]);

  // Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // If no user is logged in, don't render anything
  if (!user) {
    return null;
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          profileImage: reader.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    // Validate form
    if (!formData.name || !formData.email || !formData.phone) {
      setModalMessage({
        title: 'Validation Error',
        message: 'Please fill in all required fields'
      });
      setShowErrorModal(true);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setModalMessage({
        title: 'Invalid Email',
        message: 'Please enter a valid email address'
      });
      setShowErrorModal(true);
      return;
    }

    // Show confirmation modal
    setShowSaveConfirmation(true);
  };

  const handleConfirmSave = async () => {
    if (!user) return;

    setIsSavingProfile(true);
    try {
      const [firstName = '', ...restName] = formData.name.trim().split(' ');
      const lastName = restName.join(' ').trim();

      const response = await authService.updateProfile({
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        email: formData.email,
        phone: formData.phone,
        bio: formData.bio,
        location: formData.location,
        avatar: formData.profileImage,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to update profile');
      }

      const updatedBackendUser = response.data;
      const updatedUser = {
        ...user,
        name: `${updatedBackendUser.firstName || ''} ${updatedBackendUser.lastName || ''}`.trim() || formData.name,
        email: updatedBackendUser.email || formData.email,
        phone: (updatedBackendUser as any).phone || formData.phone,
        bio: (updatedBackendUser as any).bio ?? formData.bio,
        location: (updatedBackendUser as any).location ?? formData.location,
        avatar: updatedBackendUser.avatar || formData.profileImage,
      };

      updateUser(updatedUser);
      const savedProfileData = {
        ...formData,
        profileImage: updatedUser.avatar || DEFAULT_AVATAR_URL,
      };
      setProfileData(savedProfileData);
      setFormData(savedProfileData);
      setIsEditing(false);
      setShowSaveConfirmation(false);

      setModalMessage({
        title: 'Profile Updated',
        message: 'Your profile has been updated successfully!'
      });
      setShowSuccessModal(true);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      setModalMessage({
        title: 'Update Failed',
        message: error?.message || 'Failed to save profile changes. Please try again.'
      });
      setShowErrorModal(true);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordUpdate = () => {
    // Validate password fields
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setModalMessage({
        title: 'Validation Error',
        message: 'Please fill in all password fields'
      });
      setShowErrorModal(true);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setModalMessage({
        title: 'Password Mismatch',
        message: 'New passwords do not match'
      });
      setShowErrorModal(true);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setModalMessage({
        title: 'Weak Password',
        message: 'Password must be at least 8 characters long'
      });
      setShowErrorModal(true);
      return;
    }

    // Show confirmation modal
    setShowPasswordConfirmation(true);
  };

  const handleConfirmPasswordUpdate = async () => {
    setIsLoadingPasswordChange(true);
    try {
      const response = await authService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      if (response.success) {
        setShowPasswordConfirmation(false);
        setModalMessage({
          title: 'Password Updated',
          message: 'Your password has been updated successfully!'
        });
        setShowSuccessModal(true);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordSection(false);
      } else {
        setModalMessage({
          title: 'Update Failed',
          message: response.message || 'Failed to update password. Please try again.'
        });
        setShowErrorModal(true);
      }
    } catch (error: any) {
      setModalMessage({
        title: 'Error',
        message: error?.message || 'An error occurred while updating your password. Please try again.'
      });
      setShowErrorModal(true);
    } finally {
      setIsLoadingPasswordChange(false);
    }
  };

  const handleCancel = () => {
    setFormData(profileData);
    setIsEditing(false);
  };

  const handleLogout = () => {
    // Navigate to logout which will trigger the LogoutConfirmationModal in App.tsx
    onNavigate?.('logout');
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        activePage="profile"
        onNavigate={onNavigate}
        ordersCount={ordersData.length}
      />
      
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar 
          onMenuClick={() => setSidebarOpen(!sidebarOpen)} 
          sidebarOpen={sidebarOpen} 
          onNavigate={onNavigate}
        />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Page Header */}
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Profile</h1>
                <p className="text-gray-400 dark:text-gray-500 mt-1 text-sm">Manage your personal information and settings</p>
              </div>
              
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Profile
                </button>
              )}
            </div>

            {/* Success Message */}
            {saveSuccess && (
              <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-green-900 dark:text-green-100">Profile updated successfully!</p>
                  <p className="text-xs text-green-700 dark:text-green-300 mt-0.5">Your changes have been saved.</p>
                </div>
              </div>
            )}

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Left Column */}
            <div className="space-y-6">

            {/* Profile Card */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
              {/* Cover Image */}
              <div className="h-32 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>
              
              {/* Profile Content */}
              <div className="px-6 pb-6">
                {/* Profile Image */}
                <div className="relative -mt-16 mb-4">
                  <div className="relative inline-block">
                    <img
                      src={isEditing ? formData.profileImage : profileData.profileImage}
                      alt="Profile"
                      className="w-32 h-32 rounded-full border-4 border-white dark:border-gray-900 object-cover"
                    />
                    {isEditing && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute bottom-2 right-2 p-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors shadow-lg"
                      >
                        <Camera className="w-4 h-4" />
                      </button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* Profile Info */}
                <div className="space-y-4">
                  {/* Name and Role */}
                  <div>
                    {isEditing ? (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Enter your full name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            <div className="flex items-center gap-2">
                              Role
                              <div className="relative group">
                                <Shield className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help" />
                                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg z-10">
                                  Your role can only be changed by an administrator from the Users Management page.
                                </div>
                              </div>
                            </div>
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={formData.role.charAt(0).toUpperCase() + formData.role.slice(1)}
                              disabled
                              className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 rounded-lg cursor-not-allowed"
                              placeholder="Enter your role"
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <Shield className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                            </div>
                          </div>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Shield className="w-3 h-3" />
                            Roles can only be changed by administrators
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{profileData.name}</h2>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-gray-500 dark:text-gray-400 capitalize">{profileData.role}</p>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            user.role === 'admin' 
                              ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                              : user.role === 'manager'
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                              : user.role === 'staff'
                              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                          }`}>
                            {user.role === 'admin' ? 'Full Access' : user.role === 'manager' ? 'Manager' : user.role === 'staff' ? 'Staff' : 'View Only'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Bio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bio
                    </label>
                    {isEditing ? (
                      <textarea
                        value={formData.bio}
                        onChange={(e) => handleInputChange('bio', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                        placeholder="Tell us about yourself"
                      />
                    ) : (
                      <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                        {profileData.bio || <span className="italic text-gray-400 dark:text-gray-500">No bio added yet</span>}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSave}
                  disabled={isSavingProfile}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-5 h-5" />
                  {isSavingProfile ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isSavingProfile}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-5 h-5" />
                  Cancel
                </button>
              </div>
            )}

            </div> {/* /Left Column */}

            {/* Right Column */}
            <div className="lg:col-span-2 space-y-6">

            {/* Contact Information */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email Address *
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter your email"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      {profileData.email}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone Number *
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="+254700000000"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      {profileData.phone}
                    </p>
                  )}
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Location
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g. Nairobi, Kenya"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      {profileData.location || <span className="italic text-gray-400 dark:text-gray-500">Not set</span>}
                    </p>
                  )}
                </div>

                {/* Join Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Member Since
                  </label>
                  <p className="text-gray-900 dark:text-white px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    {new Date(profileData.joinDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Security Section */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Security
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage your password and security settings</p>
                </div>
                {!showPasswordSection && (
                  <button
                    onClick={() => setShowPasswordSection(true)}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                  >
                    Change Password
                  </button>
                )}
              </div>

              {showPasswordSection && (
                <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordData.currentPassword}
                        onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                        className="w-full px-4 py-2.5 pr-11 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 px-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                        className="w-full px-4 py-2.5 pr-11 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Enter new password (min. 8 characters)"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 px-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                        className="w-full px-4 py-2.5 pr-11 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 px-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <button
                      onClick={handlePasswordUpdate}
                      disabled={isLoadingPasswordChange}
                      className="flex-1 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoadingPasswordChange ? 'Updating...' : 'Update Password'}
                    </button>
                    <button
                      onClick={() => {
                        setShowPasswordSection(false);
                        setPasswordData({
                          currentPassword: '', 
                          newPassword: '',
                          confirmPassword: '',
                        });
                      }}
                      disabled={isLoadingPasswordChange}
                      className="flex-1 px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Logout Section */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <LogOut className="w-5 h-5" />
                    Logout
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Sign out from your account</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </div>
            </div>
            </div> {/* /Right Column */}
            </div> {/* /Grid */}
          </div>
        </main>
      </div>

      {/* Save Profile Confirmation Modal */}
      {showSaveConfirmation && (
        <ConfirmationModal
          title="Save Profile Changes"
          message="Are you sure you want to save these changes to your profile?"
          confirmText="Save Changes"
          onConfirm={handleConfirmSave}
          onClose={() => setShowSaveConfirmation(false)}
          icon={<Save className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />}
        />
      )}

      {/* Update Password Confirmation Modal */}
      {showPasswordConfirmation && (
        <ConfirmationModal
          title="Update Password"
          message="Are you sure you want to update your password?"
          confirmText="Update Password"
          onConfirm={handleConfirmPasswordUpdate}
          onClose={() => setShowPasswordConfirmation(false)}
          icon={<Lock className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal
          title={modalMessage.title}
          message={modalMessage.message}
          onClose={() => setShowSuccessModal(false)}
        />
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <SuccessModal
          title={modalMessage.title}
          message={modalMessage.message}
          onClose={() => setShowErrorModal(false)}
          icon={<AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />}
        />
      )}
    </div>
  );
}